import { ref, readonly, type Ref } from "vue";
import { useToast } from "primevue/usetoast";
import { updateUser } from "../api";
import type { PatchedAdminProfile } from "@/api/model";

interface UseUpdateUserReturn {
  readonly isSubmitting: Ref<boolean>;
  updateUser: (
    userId: number,
    data: PatchedAdminProfile,
    organizationId?: number
  ) => Promise<boolean>;
}

/**
 * Composable for updating user information
 */
export function useUpdateUser(): UseUpdateUserReturn {
  const toast = useToast();
  const isSubmitting = ref(false);

  async function updateUserData(
    userId: number,
    data: PatchedAdminProfile,
    organizationId?: number
  ): Promise<boolean> {
    if (!userId) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "User ID is required",
        life: 5000,
      });
      return false;
    }

    isSubmitting.value = true;

    try {
      const result = await updateUser(userId, data, organizationId);

      if ("error" in result) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: result.error || "Failed to update user",
          life: 5000,
        });
        return false;
      }

      toast.add({
        severity: "success",
        summary: "Success",
        detail: "User has been updated successfully",
        life: 3000,
      });

      return true;
    } catch (error) {
      console.error("Error updating user:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "An unexpected error occurred",
        life: 5000,
      });
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  return {
    isSubmitting: readonly(isSubmitting),
    updateUser: updateUserData,
  };
}
